import {
  type GetWorkplaceExternalIntegrationsResponse,
  GetWorkplaceExternalIntegrationsResponseSchema,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetParsedQuery } from "@src/appV2/api/useGetParsedQuery";
import { environmentConfig } from "@src/appV2/environment";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

export type WorkplaceExternalIntegration = GetWorkplaceExternalIntegrationsResponse["data"][number];

export function getWorkplaceExternalIntegrationsUrl(workplaceId: string): string {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/external-integrations`;
}

// Local schema instead of GetWorkplaceExternalIntegrationsQuerySchema because the contract
// schema has a default of "all" for filter.enabled, which causes it to always be sent as a
// query param even when no filtering is intended. Once "all" is removed from the contract,
// this can be replaced with GetWorkplaceExternalIntegrationsQuerySchema.
const queryParamsSchema = z.object({
  filter: z
    .object({
      enabled: z.enum(["true", "false"]),
    })
    .optional(),
});

interface UseGetWorkplaceExternalIntegrationsParams {
  workplaceId: string;
  enabled?: boolean;
  /**
   * Filter by enabled status. When undefined, returns all integrations regardless of status.
   */
  filterEnabled?: boolean;
}

export function useGetWorkplaceExternalIntegrations(
  params: UseGetWorkplaceExternalIntegrationsParams
): UseQueryResult<WorkplaceExternalIntegration[]> {
  const { workplaceId, enabled, filterEnabled } = params;

  const queryParams = isDefined(filterEnabled)
    ? { filter: { enabled: filterEnabled ? ("true" as const) : ("false" as const) } }
    : {};

  return useGetParsedQuery({
    url: getWorkplaceExternalIntegrationsUrl(workplaceId),
    queryParams,
    queryParamsSchema,
    responseSchema: GetWorkplaceExternalIntegrationsResponseSchema,
    enabled,
    meta: {
      logErrorMessage: "GET_WORKPLACE_EXTERNAL_INTEGRATIONS_FAILURE",
    },
    select: (response) => response.data,
  });
}
